<template>
  <PublicLayout
    variant="no-bg"
    :hero="false"
    breadcrumbs-in-top
    :ui="{
      main: 'pt-0!',
    }"
  >
    <UContainer
      :class="{
        'common-mb': subdirections.length,
      }"
    >
      <SPBreadcrumbs dark :breadcrumbs="breadcrumbs" />

      <PageContentSlider
        :class="{
          'common-mb': subdirections.length,
        }"
        :title="direction?.name ?? ''"
        :description="direction.description ?? ''"
        :slides="competitionsWithLinks || []"
        to-prefix="/directions"
        :loading="pending"
        :ui="{
          description: 'line-clamp-5',
          contentCard: 'w-103!',
          contentCardBody: 'flex flex-col h-full',
          title: 'hidden!',
          featuredCardTitle: 'mb-0!',
          featuredCardInfo: 'mb-4 max-md:mb-5!',
          featuredCardDescription: 'hidden',
        }"
      >
        <template #slide-badges="{ slide }">
          <UBadge
            class="rounded-[250px] text-white! ring-white bg-transparent"
            variant="subtle"
            icon="i-local-calendar"
            :ui="{
              leadingIcon: 'text-white',
            }"
          >
            {{ dayjs(slide.starts_at).format('DD MMMM YYYY') }}
          </UBadge>
        </template>

        <template #slide-button>
          <UButton
            class="mb-10.5 max-lg:w-max text-button-secondary-foreground max-sm:hidden"
            trailing-icon="i-local-move-up-right"
            color="white"
            to="#"
            :ui="{
              trailingIcon: 'text-button-secondary-foreground',
            }"
          >
            Мокдробнее
          </UButton>
        </template>

        <template #title>
          <div class="flex max-lg:gap-5 max-md:gap-4 max-lg:mb-7.5">
            <CardDirection
              class="mb-4 mr-4 max-md:mr-2 shrink-0 lg:hidden max-[429px]:hidden"
              :slug="direction.slug"
            />

            <div>
              <div class="flex items-center mb-4 max-md:mb-2">
                <CardDirection
                  class="mr-6 max- shrink-0 max-lg:hidden max-[429px]:block!"
                  :slug="direction.slug"
                />
                <h2 class="typo-heading-h2">
                  {{ direction.name }}
                </h2>
              </div>
              <p
                class="font-lato! typo-paragraph-md-regular text-content-default-secondary line-clamp-8"
              >
                {{ direction.description }}
              </p>
            </div>
          </div>
        </template>

        <template #description><div /></template>

        <template #card-footer>
          <UCard
            v-if="schools.length"
            class="mb-3"
            :ui="{
              body: 'flex items-center h-22 p-5! pr-2.5!',
            }"
          >
            <UButton
              class="w-12 h-12 shrink-0 pointer-events-none mr-4 rounded-[12px]"
              icon="i-local-graduation-cap"
              :ui="{
                leadingIcon: 'w-6 h-6',
              }"
            />

            <div>
              <h3 class="font-semibold text-neutral-default">
                {{
                  declOfNumWithVal(schools.length, ['школа', 'школы', 'школ'])
                }}
              </h3>
              <p class="text-sm text-neutral-light font-lato!">
                Обучают в данном направлении
              </p>
            </div>

            <UButton
              class="w-12 h-12 shrink-0 text-neutral-light ml-auto"
              icon="i-local-chevron-right"
              variant="ghost"
              size="xl"
            />
          </UCard>

          <UCard
            v-if="trainers.length"
            :ui="{
              body: 'flex items-center h-22 p-5! pr-2.5!',
            }"
          >
            <UButton
              class="w-12 h-12 shrink-0 pointer-events-none mr-4 rounded-[12px]"
              icon="i-local-coach"
              color="warning"
              :ui="{
                leadingIcon: 'w-6 h-6',
              }"
            />

            <div>
              <h3 class="font-semibold text-neutral-default">
                {{
                  declOfNumWithVal(trainers.length, [
                    'тренер',
                    'тренера',
                    'тренеров',
                  ])
                }}
              </h3>
              <p class="text-sm text-neutral-light font-lato!">
                Со специальной классификацией
              </p>
            </div>

            <UButton
              class="w-12 h-12 shrink-0 text-neutral-light ml-auto"
              icon="i-local-chevron-right"
              variant="ghost"
              size="xl"
            />
          </UCard>
        </template>
      </PageContentSlider>
    </UContainer>

    <UContainer
      v-if="subdirections.length"
      class="subdirections common-pb border-b border-b-neutral-border-default"
    >
      <h2 class="text-size-h2 font-semibold mb-10 max-lg:mb-7">
        Дисциплины направления
      </h2>

      <ExpandableList
        v-slot="{ displayItems }"
        :items="subdirections"
        :limit="6"
      >
        <CommonGridCards :items="displayItems" :pending="pending">
          <template #card="{ item }">
            <CommonCard
              :item="item"
              :to="`/directions/${item.id}`"
              :description-under-left="descriptionUnder"
              :ui="{
                title: 'max-sm:line-clamp-1',
              }"
            >
            </CommonCard>
          </template>
        </CommonGridCards>
      </ExpandableList>
    </UContainer>

    <PopularSchoolsSection
      v-if="schools?.length"
      :schools="schools"
      :loading="pending"
      :ui="{
        image: 'max-xl:h-45!',
      }"
    />

    <BestTrainersSection
      v-if="trainers?.length"
      class="py-20 max-xl:py-15 max-md:py-12 max-lg:pb-6!"
      :trainers="trainers"
      :loading="pending"
      :ui="{
        image: 'max-xl:h-45!',
      }"
    />

    <NewsAndEventsSection
      v-if="news?.length"
      :news="news"
      :loading="pending"
      title="Новости и события направления"
      :ui="{
        card: 'max-xl:min-h-103! max-xl:h-103!',
        header: 'items-start',
        title: 'max-w-1/2',
      }"
    />

    <JoinUsSection />
  </PublicLayout>
</template>

<script setup lang="ts">
import PageContentSlider from '~/components/PageContentSlider.vue'
import PublicLayout from '~/layouts/PublicLayout.vue'

import { dayjs } from '~/shared/day'
import CardDirection from '~/components/main-page/cards/CardDirection.vue'
import PopularSchoolsSection from '~/components/main-page/PopularSchoolsSection.vue'
import BestTrainersSection from '~/components/main-page/BestTrainersSection.vue'
import NewsAndEventsSection from '~/components/main-page/NewsAndEventsSection.vue'
import JoinUsSection from '~/components/main-page/JoinUsSection.vue'
import { declOfNumWithVal } from '~/shared'
import type { IconWithLabel } from '~/types'

defineOptions({
  name: 'DirectionPage',
})

const route = useRoute()
const id = route.params.id as string

const directoryStore = useDirectoryStore()

const directionStore = useDirectionStore()
const { direction, subdirections, schools, news, competitions, trainers } =
  storeToRefs(directionStore)

const { untilReady } = useRegionReady()

const { pending: apiPending } = useAppAsyncData(`direction-${id}`, async () => {
  await untilReady()
  await Promise.all([
    directoryStore.fetchDirections(),
    directoryStore.fetchSchools(),
    directionStore.fetchDirection(id),
  ])
  return directionStore.direction
})

const pending = computed(() => apiPending.value)

const breadcrumbs = computed(() => [
  {
    name: 'Направления',
    to: '/directions/',
  },
  {
    name: direction.value?.name || '',
  },
])

const descriptionUnder: IconWithLabel[] = [
  {
    icon: 'i-local-graduation-cap',
    label: 'Школы: ',
    value: '44 мок',
    iconClass: '',
  },
  {
    icon: 'i-local-award',
    label: 'Тренеры: ',
    value: '24 мок',
    iconClass: 'bg-warning-light',
  },
]

const competitionsWithLinks = computed(() =>
  competitions.value
    .map((item) => ({
      ...item,
      links: [
        {
          icon: 'i-local-graduation-cap',
          text: item.organizer_name,
          color: 'bg-accent-default',
        },
        {
          icon: 'i-local-location',
          text: item.region?.name ?? '',
          color: 'bg-add-light',
        },
      ],
    }))
    .map((item) => {
      delete (item as any).preview_description
      return item
    }),
)
</script>

<style lang="scss" scoped>
.direction-card {
  height: 402px;
  min-height: 402px;
}

.subdirections {
  :deep(.common-card) {
    --card-height: 422px;
    --card-image-height: 240px;

    @include max-xl {
      --card-height: 358px;
      --card-image-height: 200px;
    }

    @include max-md {
      --card-height: 330px;
      --card-image-height: 160px;
    }
  }
}
</style>
